/**
 * scheduleAdapter — the single conversion point between our task model and
 * SVAR Gantt's ITask shape.
 *
 * Our model (backend, ISO strings, END INCLUSIVE):
 *   { id, trade, task_name, status, start_date, end_date, sort_order, ... }
 *   A one-day task on 12 Aug is start_date "2026-08-12", end_date "2026-08-12".
 *   Both empty = unscheduled.
 *
 * SVAR ITask (Date objects):
 *   { id, text, start, end, unscheduled, ...custom }
 *
 * Everything here is a pure function so the conversion can be tested without a
 * browser. Nothing else in the app should convert between the two shapes — the
 * old code derived dates in three places and they drifted apart.
 */
import { parseISO, format, addDays, subDays, isValid } from 'date-fns';

/**
 * Whether SVAR treats ITask.end as EXCLUSIVE (the day after the last worked day).
 *
 * Pinned by test against observed behaviour rather than assumed: a bar spanning
 * 19,20,21,22 August displayed an end of 23 August, which is exclusive.
 * If this ever turns out to be wrong, this constant is the only thing to change.
 */
export const SVAR_END_IS_EXCLUSIVE = true;

const ISO = 'yyyy-MM-dd';

/** ISO string → Date, or null when absent/invalid. */
export function isoToDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
}

/** Date → ISO string, or '' when absent/invalid. */
export function dateToIso(date) {
  if (!date || !isValid(date)) return '';
  return format(date, ISO);
}

/** Our task → SVAR ITask. Unscheduled tasks carry no start/end at all. */
export function toSvarTask(task) {
  const start = isoToDate(task.start_date);
  const endInclusive = isoToDate(task.end_date);

  const base = {
    id: task.id,
    text: task.task_name || '',
    type: 'task',
    // Custom fields ride along on ITask ([key: string]: any) so they can be
    // shown as editable columns.
    trade: task.trade || '',
    status: task.status || 'pending',
    assigned_contact_id: task.assigned_contact_id || '',
    notes: task.notes || '',
    sort_order: task.sort_order ?? 0,
  };

  if (!start || !endInclusive) {
    // No start/end keys at all — that absence is what stops the grid rendering
    // NaN and stops a bar being drawn for a task with no dates.
    return { ...base, unscheduled: true, end_display: null, duration_days: null };
  }

  return {
    ...base,
    start,
    end: SVAR_END_IS_EXCLUSIVE ? addDays(endInclusive, 1) : endInclusive,
    // The grid must NOT bind its Finish column to `end`: SVAR's end is exclusive,
    // so a task worked through the 21st displays as the 22nd and a site manager
    // plans a day too long. `end_display` is the real last working day, and the
    // Finish column binds to this instead.
    end_display: endInclusive,
    duration_days: durationDays(task.start_date, task.end_date),
  };
}

/**
 * SVAR ITask → the subset of our fields it can change.
 *
 * Returns only schedule/label fields; ids and ownership are never taken from
 * the widget. Result is shaped for PATCH /projects/{id}/tasks/{taskId}.
 */
export function fromSvarTask(svarTask) {
  const out = {};

  if ('text' in svarTask) out.task_name = svarTask.text || '';
  if ('trade' in svarTask) out.trade = svarTask.trade || '';
  if ('status' in svarTask) out.status = svarTask.status || 'pending';
  if ('assigned_contact_id' in svarTask) out.assigned_contact_id = svarTask.assigned_contact_id || '';
  if ('notes' in svarTask) out.notes = svarTask.notes || '';

  if (svarTask.unscheduled) {
    out.start_date = '';
    out.end_date = '';
    return out;
  }

  if (svarTask.start) out.start_date = dateToIso(svarTask.start);

  // Finish can arrive two ways: the user edited the Finish column (end_display,
  // inclusive) or dragged the bar's edge (end, exclusive). end_display wins,
  // because it is what the user was actually looking at.
  if (svarTask.end_display) {
    out.end_date = dateToIso(svarTask.end_display);
  } else if (svarTask.end) {
    out.end_date = dateToIso(
      SVAR_END_IS_EXCLUSIVE ? subDays(svarTask.end, 1) : svarTask.end
    );
  }

  // A finish before the start is never sent — the backend would 422 it, and the
  // user would see their edit vanish.
  if (out.start_date && out.end_date && out.end_date < out.start_date) {
    out.end_date = out.start_date;
  }

  return out;
}

/** Whole list, ordered by sort_order so the grid matches stored order. */
export function toSvarTasks(tasks) {
  return [...(tasks || [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(toSvarTask);
}

/**
 * Inclusive day count, the "Duration" column users expect.
 * A task starting and ending on the same day lasts 1 day, not 0.
 */
export function durationDays(startIso, endIso) {
  const s = isoToDate(startIso);
  const e = isoToDate(endIso);
  if (!s || !e) return null;
  return Math.round((e - s) / 86400000) + 1;
}

/**
 * Map tasks onto calendar day cells using ABSOLUTE dates.
 *
 * The old calendar derived each day from `project.start_date + start_day`, which
 * is what let the Gantt and Calendar disagree. There is no derivation here: a
 * task occupies exactly the days between its own start_date and end_date.
 *
 * Returns { 'YYYY-MM-DD': [task, ...] }. Unscheduled tasks appear nowhere —
 * callers must surface them separately or they become invisible.
 */
export function mapTasksToCalendar(tasks) {
  const dayMap = {};
  for (const task of tasks || []) {
    const start = isoToDate(task.start_date);
    const end = isoToDate(task.end_date);
    if (!start || !end || end < start) continue;

    for (let d = start; d <= end; d = addDays(d, 1)) {
      const key = dateToIso(d);
      (dayMap[key] || (dayMap[key] = [])).push(task);
    }
  }
  return dayMap;
}

/**
 * The date window the Gantt should display.
 *
 * SVAR derives its timeline from the tasks. When NO task has dates it has
 * nothing to derive from, and rendering fails with
 * "Cannot read properties of undefined (reading 'width')" — which is exactly
 * the state every existing project is in (all 52 tasks are unscheduled).
 * So the range is always supplied explicitly.
 *
 * Returns { start: Date, end: Date }, padded so bars aren't flush to the edge.
 */
export function scheduleRange(tasks, today = new Date()) {
  const starts = [];
  const ends = [];
  for (const t of tasks || []) {
    const s = isoToDate(t.start_date);
    const e = isoToDate(t.end_date);
    if (s) starts.push(s);
    if (e) ends.push(e);
  }

  if (starts.length === 0 || ends.length === 0) {
    // Nothing scheduled — show a usable window around today rather than nothing.
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return { start: addDays(base, -7), end: addDays(base, 60) };
  }

  const min = new Date(Math.min(...starts.map(d => d.getTime())));
  const max = new Date(Math.max(...ends.map(d => d.getTime())));
  return { start: addDays(min, -7), end: addDays(max, 14) };
}

/** Tasks with no dates — invisible on a calendar, so they need their own tray. */
export function unscheduledTasks(tasks) {
  return (tasks || []).filter(t => !t.start_date || !t.end_date);
}

/** Distinct tasks touching a given month (a spanning task counts once per month). */
export function countTasksInMonth(dayMap, year, monthIndex) {
  const ids = new Set();
  for (const [key, dayTasks] of Object.entries(dayMap)) {
    const d = isoToDate(key);
    if (!d || d.getFullYear() !== year || d.getMonth() !== monthIndex) continue;
    for (const t of dayTasks) ids.add(t.id);
  }
  return ids.size;
}

/**
 * Given any two of start / end / duration, derive the third.
 * This is what keeps the three columns consistent while typing.
 */
export function deriveDates({ startIso, endIso, duration, changed }) {
  const s = isoToDate(startIso);
  const e = isoToDate(endIso);

  if (changed === 'duration' && s && duration >= 1) {
    return { startIso, endIso: dateToIso(addDays(s, duration - 1)) };
  }
  if (changed === 'start' && s) {
    // Moving the start keeps the length, rather than silently stretching it.
    const len = duration && duration >= 1 ? duration : (e ? durationDays(startIso, endIso) : 1);
    return { startIso, endIso: dateToIso(addDays(s, Math.max(1, len) - 1)) };
  }
  if (changed === 'end' && s && e) {
    return e < s ? { startIso, endIso: startIso } : { startIso, endIso };
  }
  return { startIso, endIso };
}
